import { Component, Input, Inject, OnInit, OnChanges } from 'angular-ts-decorators';

import { IBasketEventBus } from '@gn-audio/sfcc-basket/models';
import { BasketEvent } from '@gn-audio/sfcc-basket/enums';
import { SiteFeature } from '@gn-audio/core/api/site-settings';
import { IProductWithAttributes, fetchGroupService, fetchGroupImages } from '@gn-audio/core/api/product-catalogue';
import { IImage } from '@gn-audio/core/api/product-catalogue/models';
import { ServiceType } from '@gn-audio/core/api/product-catalogue/enums/service-type';
import { isSiteFeatureEnabled } from '@gn-audio/core/common/utilities';
import { AvailabilityState, IFromPrice, ISFCCGroupPrice, ProductType, fetchGroupPrice, getAvailabilityState } from '@gn-audio/core';
// import { fetchProductPrice } from '@gn-audio/core';
import { isProductServiceAllowedForLocale } from '@gn-audio/sfcc-basket/utilities/is-product-service-allowed-for-locale';

import { GiftWrappingApiService } from './../gift-wrapping-api.service';
import { IGiftWrappingConfiguration } from './../models/gift-wrapping.configuration';
import { IHeroCustomizerGiftWrapping } from '../models/hero-customizer-gift-wrapping';

import template from './hero-customizer-v3-gift-wrapping.component.html';

declare var BasketEventBus: IBasketEventBus;

@Component({
  selector: 'heroCustomizerV3GiftWrapping',
  template,
})

export class HeroCustomizerV3GiftWrappingComponent implements OnInit, OnChanges {
  public static $inject: ReadonlyArray<string> = [];

  public giftWrappingMessage: string;
  public giftWrappingSku: string;
  public giftWrappingImage: string;
  public isGiftWrappingAllowed: boolean = false;
  public isGiftWrappingInfoExpanded: boolean = false;
  public isGiftWrappingSelected: boolean = false;
  public isEnabled: boolean = false;
  public giftWrapping: IHeroCustomizerGiftWrapping | null;
  public productType?: ProductType = ProductType.Product;

  @Input() public readonly group?: IProductWithAttributes;
  @Input('&') public readonly onGiftWrappingStatusChange?: (args: 
    { 
      message: string;
      sku: string;
    }) => void;

  // Prices will only be used for special types of Gift wrapping like Black Friday, Christmas etc.
  // @Input('&') public readonly onSelectedServicesChange?: (args: 
  //   { selectedServices: IHeroCustomizerGiftWrapping[] }
  // ) => void;

  constructor(
    @Inject('$scope') private readonly $scope: ng.IScope, 
    @Inject('giftWrappingApiService') private readonly giftWrappingApiService: GiftWrappingApiService,
    ) { };

  public async ngOnInit() {
    this.giftWrappingApiService
      .getGiftWrapping()
      .then(this.onConfigurationLoad.bind(this));

    this.isEnabled = await this.isGiftWrappingFeatureOn();
    if (!this.isEnabled) {
      return;
    }
    
    if (typeof BasketEventBus === 'undefined') {
      window.addEventListener(BasketEvent.EventBusReady, this.subscribe);
    } else {
      this.subscribe();
    }
  };

  private onConfigurationLoad(configuration: IGiftWrappingConfiguration) {
    this.giftWrappingImage = configuration.giftWrappingFallbackImage;
    this.giftWrappingMessage = configuration.giftWrappingD365String;
  }

  private subscribe = () => {
    BasketEventBus.subscribe(BasketEvent.AddToBasket, this.clearGiftWrapping);
  };

  private clearGiftWrapping = () => {
    // Empty gift wrapping message when product is added to the basket.
    this.isGiftWrappingInfoExpanded = false;
    this.isGiftWrappingSelected = false;
    this.giftWrapping.isSelected = false;
    this.applyGiftWrapping();
  };

  public async ngOnChanges() {
    this.isEnabled = await this.isGiftWrappingFeatureOn();
    await this.handleChanges();
    this.$scope.$applyAsync();
  }

  private async isGiftWrappingFeatureOn(): Promise<boolean> {
    return await isSiteFeatureEnabled(SiteFeature.SfccBasketGiftWrapping);
  }

  private async handleChanges() {
    if (!this.isEnabled) {
      return;
    }

    if (!this.group) {
      this.isGiftWrappingAllowed = false;
      return;
    }

    this.giftWrapping = await fetchGroupService(this.group.groupId, ServiceType.GiftWrapping);
    if (!this.giftWrapping) {
      this.isGiftWrappingAllowed = false;
      return;
    }   

    // Prices will only be used for special types of Gift wrapping like Black Friday, Christmas etc.
    // this.giftWrapping.price = await fetchProductPrice(this.giftWrapping.productId);
    this.giftWrappingSku = this.giftWrapping.sku;
    await this.setGiftWrappingImageUrl(this.giftWrapping.groupId);
    const groupPrice: IFromPrice | ISFCCGroupPrice | null = await fetchGroupPrice(this.group.groupId);
    const availabilityState: AvailabilityState = getAvailabilityState(groupPrice);
    const isGiftWrappingAllowedOnLocale: boolean = await isProductServiceAllowedForLocale(this.group.sku, ServiceType.GiftWrapping);
    this.isGiftWrappingAllowed = isGiftWrappingAllowedOnLocale && availabilityState <= AvailabilityState.BackOrder;
    this.isGiftWrappingSelected = false;
    this.giftWrapping.isSelected = false;
    this.applyGiftWrapping();
  }

  public async setGiftWrappingImageUrl(giftWrappingGroupId: number): Promise<void> {
    const giftWrappingImages: IImage[] = await fetchGroupImages(giftWrappingGroupId);
    if (!giftWrappingImages?.length) {
      return;
    }

    this.giftWrappingImage = giftWrappingImages[0].url;
  }

  public expandGiftWrappingInfo() {
    this.isGiftWrappingInfoExpanded = true;
  }

  public collapseGiftWrappingInfo() {
    this.isGiftWrappingInfoExpanded = false;
  }

  public async addGiftWrapping() {
    this.isGiftWrappingSelected = true;
    this.giftWrapping.isSelected = true;
    this.applyGiftWrapping();
  }

  public async removeGiftWrapping() {
    this.isGiftWrappingSelected = false;
    this.giftWrapping.isSelected = false;
    this.applyGiftWrapping();
  }

  private applyGiftWrapping = () => {
    if (this.onGiftWrappingStatusChange) {
      this.onGiftWrappingStatusChange({ 
        message: this.isGiftWrappingSelected ? this.giftWrappingMessage : null, 
        sku: this.giftWrappingSku});
    }

    // Prices will only be used for special types of Gift wrapping like Black Friday, Christmas etc.
    // const selectedServices: IHeroCustomizerGiftWrapping[] = this.isGiftWrappingSelected ? [this.giftWrapping] : null;

    // if (this.onSelectedServicesChange) {
    //   this.onSelectedServicesChange({ selectedServices })
    // }

    this.$scope.$applyAsync();
  };
}
